"use client";

import { m, useReducedMotion } from "motion/react";
import Image from "next/image";

import { mergeClasses } from "@trex-2b/libs";

type BenefitsListProps = {
  className?: string;
};

/**
 * Figma `8788:9937` — three benefit stacks; dividers after items 1 and 2 only (`8788:9941`, `8788:9945`).
 */
function BenefitsList({ className }: BenefitsListProps) {
  const shouldReduceMotion = useReducedMotion();

  return (
    <m.div
      className={mergeClasses(
        // Figma `8788:9937`: 544×555; inset y=40.5 top & bottom; blocks spaced +46px (`8788:9938`→`8788:9942` gap)
        "flex w-full max-w-[544px] flex-col items-stretch gap-[46px] pt-[40.5px] pb-[40.5px] font-inter lg:w-[544px] lg:max-w-none lg:shrink-0",
        className,
      )}
      initial={shouldReduceMotion ? "visible" : "hidden"}
      whileInView="visible"
      viewport={{ amount: 0.35, once: true }}
      variants={{
        hidden: {},
        visible: { transition: { delayChildren: 0.08, staggerChildren: 0.1 } },
      }}
    >
      <m.div
        className="flex w-full flex-col gap-[12px]"
        variants={{
          hidden: { opacity: 0, y: 20 },
          visible: { opacity: 1, y: 0 },
        }}
      >
        <p className="text-[24px] font-medium leading-[30.8px] tracking-[-0.3px] text-[#31373d]">
          They chose you back.
        </p>
        <p className="max-w-[525px] text-[20px] font-normal leading-[30.8px] tracking-[-0.3px] text-[#383838]">
          Instead of mercenaries chasing your rewards, only qualified profiles
          reach your campaign, because the fit is mutual.
        </p>
        {/* 1px 分割线：Figma `Vector 5879`；用任意色值确保 Tailwind v4 一定产出规则（`bg-v2-border-*` 嵌套 token 在此处可能未编入 CSS） */}
        <div
          className="h-px min-h-px w-full shrink-0 bg-[#e6e6e6]"
          aria-hidden
        />
      </m.div>

      <m.div
        className="flex w-full flex-col gap-[12px]"
        variants={{
          hidden: { opacity: 0, y: 20 },
          visible: { opacity: 1, y: 0 },
        }}
      >
        <p className="text-[24px] font-medium leading-[30.8px] tracking-[-0.3px] text-[#31373d]">
          They stay after the campaign ends.
        </p>
        <p className="max-w-[525px] text-[20px] font-normal leading-[30.8px] tracking-[-0.3px] text-[#383838]">
          41% still active at 30 days. No more retention cliffs when the
          incentives stop.
        </p>
        {/* 1px 分割线：Figma `Vector 5879`；用任意色值确保 Tailwind v4 一定产出规则（`bg-v2-border-*` 嵌套 token 在此处可能未编入 CSS） */}
        <div
          className="h-px min-h-px w-full shrink-0 bg-[#e6e6e6]"
          aria-hidden
        />
      </m.div>

      <m.div
        className="flex w-full flex-col gap-[12px]"
        variants={{
          hidden: { opacity: 0, y: 20 },
          visible: { opacity: 1, y: 0 },
        }}
      >
        <p className="text-[24px] font-medium leading-[30.8px] tracking-[-0.3px] text-[#31373d]">
          They bring their people.
        </p>
        <p className="max-w-[525px] text-[20px] font-normal leading-[30.8px] tracking-[-0.3px] text-[#383838]">
          Verified social reach and referral quality mean your community
          compounds organically.
        </p>
      </m.div>
    </m.div>
  );
}

type RolloutsSectionProps = {
  className?: string;
};

/**
 * Figma `8788:6432` RolloutsSection — 1512×824, px=145 py=134.5, `8788:6433` 660×555 + 18px + `8788:9937` 544×555.
 * Left column: placeholder raster per PO (`8788:6433`).
 */
export function RolloutsSection({ className }: RolloutsSectionProps) {
  const shouldReduceMotion = useReducedMotion();

  return (
    <section
      className={mergeClasses(
        // Figma frame width 1512 — match `LandingStepsViewPager` rhythm
        "w-full  bg-white text-black font-inter",
        "lg:h-[824px]",
        className,
      )}
    >
      <div
        className={mergeClasses(
          "flex h-full w-full flex-col items-stretch gap-10 px-8 py-12",
          "lg:flex-row lg:items-start lg:gap-[18px] lg:py-[134.5px] justify-center",
        )}
      >
        <m.div
          className="relative h-[555px] w-full max-w-[660px] shrink-0 max-lg:mx-auto max-lg:aspect-660/555 max-lg:h-auto lg:mx-0"
          initial={shouldReduceMotion ? undefined : { opacity: 0, x: -36 }}
          whileInView={shouldReduceMotion ? undefined : { opacity: 1, x: 0 }}
          viewport={{ amount: 0.3, once: true }}
          transition={{ duration: 0.68, ease: [0.16, 1, 0.3, 1] }}
        >
          <Image
            src="/images/landing/bg-roll.webp"
            alt="Rollouts illustration (placeholder)"
            fill
            className="object-contain object-top-left"
            sizes="(max-width: 1024px) min(660px, 100vw), 660px"
          />
        </m.div>
        <BenefitsList />
      </div>
    </section>
  );
}
